import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  patientCategoryService,
  type PatientCategoryListParams,
} from '../patientCategoryService';

/**
 * Every patient category (no pagination).
 *
 * Used where the full list is needed to resolve a stored category key to its
 * label — e.g. the patient detail page. The Categories management screen uses
 * `usePatientCategoryList` for the paginated view instead.
 */
export function usePatientCategories() {
  return useQuery({
    queryKey: ['patient-categories', 'all'],
    queryFn: () => patientCategoryService.getAll(),
    staleTime: 30000,
  });
}

/** Paginated + searchable + filterable management list with the trash count. */
export function usePatientCategoryList(params: PatientCategoryListParams = {}) {
  return useQuery({
    queryKey: ['patient-categories', 'list', params],
    queryFn: () => patientCategoryService.getAll(params),
    placeholderData: (previous) => previous,
    staleTime: 30000,
  });
}

export function useActivePatientCategories() {
  return useQuery({
    queryKey: ['patient-categories', 'active'],
    queryFn: () => patientCategoryService.getActive(),
    staleTime: 30000,
  });
}

/** Paginated + searchable trashed categories (the Trash page). */
export function useTrashedPatientCategories(page = 1, limit = 20, search?: string) {
  return useQuery({
    queryKey: ['patient-categories', 'trash', page, limit, search],
    queryFn: () => patientCategoryService.getTrashed(page, limit, search),
    placeholderData: (previous) => previous,
    staleTime: 30000,
  });
}

export function useCreatePatientCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: patientCategoryService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patient-categories'] });
    },
  });
}

export function useUpdatePatientCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => patientCategoryService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patient-categories'] });
    },
  });
}

/** Soft delete — moves a category to the Trash. */
export function useDeletePatientCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: patientCategoryService.remove,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patient-categories'] });
    },
  });
}

export function useRestorePatientCategories() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => patientCategoryService.restoreCategories(ids),
    meta: { hideToast: true },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patient-categories'] });
    },
  });
}

export function usePermanentlyDeletePatientCategories() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => patientCategoryService.permanentlyDeleteCategories(ids),
    meta: { hideToast: true },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patient-categories'] });
    },
  });
}
